import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Truck } from "lucide-react";
import { api, formatShort } from "@/lib/api";
import { useLiveRefresh } from "@/lib/live";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";

const n = (v) => Number(v || 0).toLocaleString("en-IN", { maximumFractionDigits: 0 });

/** Tile for every dashboard: how many jobs are part-dispatched and how much is still to send. Click it for the list:
 *  each job with its order quantity, what was dispatched and what is left to send. */
export default function PartialDispatch() {
  const [d, setD] = useState(null);
  const [open, setOpen] = useState(false);
  const [shown, setShown] = useState(null); // job whose dispatch detail is open
  const load = () => api.get("/dashboard/partial-dispatch").then(({ data }) => setD(data)).catch(() => setD((x) => x || { count: 0, rows: [], ordered: 0, sent: 0, to_send: 0 }));
  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps
  useLiveRefresh("jobs", load);
  if (!d) return null;
  return (
    <>
      <button type="button" onClick={() => setOpen(true)} data-testid="kpi-partial-dispatch"
        className="block h-full w-full text-left border border-neutral-200 bg-white px-3 py-2.5 md:px-4 md:py-3 hover:border-[#FF5A00] transition-colors">
        <div className="flex items-start justify-between">
          <div className="label-caps text-[10px] leading-tight">Partially dispatched</div>
          <Truck size={13} strokeWidth={1.75} className="text-neutral-400 shrink-0" />
        </div>
        <div className={`font-mono-num text-lg md:text-xl font-bold mt-1.5 tracking-tight ${d.count ? "text-amber-700" : "text-neutral-900"}`}>{n(d.count)}</div>
        <div className="text-[11px] text-neutral-500 mt-0.5 truncate">{d.count ? `${formatShort(d.to_send)} still to send` : "no job part-dispatched"}</div>
      </button>

      <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) setShown(null); }}>
        <DialogContent className="max-w-5xl rounded-sm max-h-[85vh] overflow-y-auto" data-testid="partial-dispatch-dialog">
          <DialogHeader>
            <DialogTitle className="font-display">Partially dispatched jobs · {n(d.count)}</DialogTitle>
            <DialogDescription>
              {n(d.sent)} dispatched of {n(d.ordered)} ordered: <b className="text-amber-700">{n(d.to_send)} still to send</b>. Oldest dispatch first; click a job for its dispatch details.
            </DialogDescription>
          </DialogHeader>
          {d.count === 0 && <div className="py-8 text-center text-sm text-neutral-400">No job is part-dispatched.</div>}
          {d.count > 0 && (
            <div className="overflow-x-auto border border-neutral-200">
              <table className="w-full text-sm" data-testid="partial-dispatch-table">
                <thead className="bg-neutral-50 label-caps text-[10px]">
                  <tr>
                    <th className="text-left px-3 py-2">BPR</th>
                    <th className="text-left px-3 py-2">Customer · product</th>
                    <th className="text-right px-3 py-2">Ordered</th>
                    <th className="text-right px-3 py-2">Dispatched</th>
                    <th className="text-right px-3 py-2">Still to send</th>
                    <th className="text-left px-3 py-2 whitespace-nowrap">Last dispatch</th>
                  </tr>
                </thead>
                <tbody>
                  {d.rows.map((r) => [
                    <tr key={r.id} className="border-t border-neutral-100 hover:bg-neutral-50 cursor-pointer" onClick={() => setShown(shown === r.id ? null : r.id)} data-testid="partial-dispatch-row">
                      <td className="px-3 py-2 font-mono-num text-xs font-semibold whitespace-nowrap">
                        <Link to={`/orders/${r.id}`} onClick={(e) => e.stopPropagation()} className="hover:text-[#FF5A00] hover:underline">{r.mpwo_number}</Link>
                        {r.where && <div className="text-[10px] font-normal text-neutral-500 uppercase tracking-wider">balance: {r.where}</div>}
                      </td>
                      <td className="px-3 py-2">
                        <div className="text-sm font-medium truncate max-w-xs">{r.customer_name}</div>
                        <div className="text-xs text-neutral-500 truncate max-w-xs">{r.product_name}</div>
                      </td>
                      <td className="px-3 py-2 text-right font-mono-num">{n(r.ordered)}</td>
                      <td className="px-3 py-2 text-right font-mono-num">{n(r.sent)}</td>
                      <td className={`px-3 py-2 text-right font-mono-num font-semibold ${r.to_send ? "text-amber-700" : "text-emerald-700"}`}>{r.to_send ? n(r.to_send) : "complete"}</td>
                      <td className="px-3 py-2 font-mono-num text-xs whitespace-nowrap">{String(r.dispatch_date || "—").slice(0, 10)}</td>
                    </tr>,
                    shown === r.id && (
                      <tr key={`${r.id}-d`} className="bg-neutral-50/70">
                        <td colSpan={6} className="px-4 py-2 text-xs text-neutral-700 space-y-1">
                          <div>Invoice <b className="font-mono-num">{r.invoice_number || "—"}</b> · {r.no_of_boxes ? `${r.no_of_boxes} boxes` : "boxes not recorded"}</div>
                          {r.lots_sent?.length > 0 && <div>Sent as {r.lots_sent.join(", ")}; the balance is on {r.mpwo_number}{r.where ? ` (${r.where})` : ""}.</div>}
                          {r.rolls_details && <pre className="font-mono-num text-[11px] whitespace-pre-wrap text-neutral-600">{r.rolls_details}</pre>}
                        </td>
                      </tr>
                    ),
                  ])}
                </tbody>
              </table>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
